import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowLeft, ArrowRight } from "lucide-react";
import { lenisRef } from "../lib/smooth";
import { Brochure } from "./Brochure";

const Ctx = createContext({ open: () => {} });
export const useLightbox = () => useContext(Ctx);

const NavBtn = ({ onClick, label, children, testid }) => (
  <button onClick={onClick} aria-label={label} data-testid={testid} className="flex h-12 w-12 items-center justify-center rounded-full border border-stone-600 text-paper transition-colors duration-300 hover:border-accent hover:bg-accent">
    {children}
  </button>
);

export const LightboxProvider = ({ children }) => {
  const [state, setState] = useState(null);
  const open = useCallback((images, index = 0, mode = "images") => setState({ images, index, mode }), []);
  const close = useCallback(() => setState(null), []);
  const step = useCallback((d) => setState((s) => s && { ...s, index: (s.index + d + s.images.length) % s.images.length }), []);

  useEffect(() => {
    if (!state) return undefined;
    lenisRef.current?.stop();
    const isBook = state.mode === "brochure";
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (isBook) return;
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      lenisRef.current?.start();
    };
  }, [state, close, step]);

  const many = state?.mode !== "brochure" && (state?.images?.length ?? 0) > 1;
  return (
    <Ctx.Provider value={{ open }}>
      {children}
      <AnimatePresence>
        {state && (
          <motion.div
            className="fixed inset-0 z-[120] flex flex-col bg-[rgba(20,20,20,0.94)] backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={close}
            data-testid="lightbox"
          >
            <div className="flex items-center justify-between px-5 py-4 font-mono text-[11px] uppercase tracking-[0.18em] text-stone-300 md:px-10 md:py-6" onClick={(e) => e.stopPropagation()}>
              <span data-testid="lightbox-counter">{state.mode === "brochure" ? "Spec brochure" : `${String(state.index + 1).padStart(2, "0")} / ${String(state.images.length).padStart(2, "0")}`}</span>
              <NavBtn onClick={close} label="Close" testid="lightbox-close-button"><X size={18} /></NavBtn>
            </div>
            {state.mode === "brochure" ? <Brochure pages={state.images} /> : (
            <>
            <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 md:px-24">
              <AnimatePresence initial={false}>
                <motion.img
                  key={state.images[state.index]}
                  src={state.images[state.index]}
                  alt=""
                  onClick={(e) => e.stopPropagation()}
                  drag={many ? "x" : false}
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.4}
                  onDragEnd={(_, i) => { if (i.offset.x < -60) step(1); else if (i.offset.x > 60) step(-1); }}
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.03 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute max-h-full max-w-[calc(100%-2rem)] cursor-grab object-contain md:max-w-[calc(100%-12rem)]"
                  data-testid="lightbox-image"
                />
              </AnimatePresence>
            </div>
            <div className="flex items-center justify-center gap-4 py-5 md:py-8" onClick={(e) => e.stopPropagation()}>
              {many && (
                <>
                  <NavBtn onClick={() => step(-1)} label="Previous image" testid="lightbox-prev-button"><ArrowLeft size={18} /></NavBtn>
                  <NavBtn onClick={() => step(1)} label="Next image" testid="lightbox-next-button"><ArrowRight size={18} /></NavBtn>
                </>
              )}
            </div>
            </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Ctx.Provider>
  );
};
